import { cn } from '../../lib/utils';
import {
  cpPercentHeatmapColor,
  endlessStyle,
  runningSegmentBarColorClass,
  runningSegmentBarCssColor,
  runningSegmentUsesCpHeatmapBarColor,
  splitBarHeightPercent,
  splitTitleParts,
  splitWeight,
  vizSplitCpForHeatmap,
} from '../../utils/runningWorkoutVizShared';

/**
 * @param {Array<{ duration_sec?: number, distance_m?: number, run_intensity_class?: string, intensity_norm?: number, cp_percent?: number | null, rpe?: number }>} splits
 * @param {boolean} [fillHeight] — grow to parent height (e.g. catalog cards); uses min-h from heightPx when set
 * @param {number | null} [highlightedBarIndex] — cross-highlight from map / laps
 * @param {(index: number | null) => void} [onBarHover]
 */
export default function RunningWorkoutViz({
  splits,
  className,
  heightPx = 44,
  fillHeight = false,
  highlightedBarIndex = null,
  onBarHover,
}) {
  const list = Array.isArray(splits) ? splits : [];
  const heightStyle = fillHeight ? undefined : { height: heightPx };
  if (list.length === 0) {
    return (
      <div
        className={cn(
          'w-full rounded-md bg-muted/40 border border-border/60',
          fillHeight && 'min-h-[48px] h-full',
          className
        )}
        style={heightStyle}
      />
    );
  }

  const weights = list.map((s) => splitWeight(s));
  const sumW = weights.reduce((a, b) => a + b, 0) || 1;

  return (
    <div
      className={cn(
        'flex w-full items-end gap-px rounded-md border border-border/50 bg-muted/20 px-0.5 pb-0.5',
        fillHeight && 'min-h-[48px] h-full',
        className
      )}
      style={heightStyle}
      role="img"
      aria-label="Workout intensity profile"
    >
      {list.map((s, i) => {
        const flexGrow = weights[i] / sumW;
        const barH = splitBarHeightPercent(s);
        const title = splitTitleParts(s);
        const hi = highlightedBarIndex === i;
        const heatmapBar = runningSegmentUsesCpHeatmapBarColor(s.run_intensity_class);
        return (
          <div
            key={i}
            className={cn(
              'flex min-w-[2px] flex-col justify-end self-stretch rounded-[3px] transition-[box-shadow,outline]',
              hi && 'ring-2 ring-amber-500 ring-offset-2 ring-offset-background z-[1]'
            )}
            style={{ flex: `${flexGrow} 1 0` }}
            title={title}
            onMouseEnter={() => onBarHover?.(i)}
            onMouseLeave={() => onBarHover?.(null)}
          >
            <div
              className={cn(
                'w-full min-h-[3px] rounded-[2px] transition-[height,box-shadow,outline]',
                !(heatmapBar || s.endless) && runningSegmentBarColorClass(s.run_intensity_class)
              )}
              style={{
                height: `${barH}%`,
                ...(s.endless
                  ? endlessStyle(heatmapBar ? cpPercentHeatmapColor(vizSplitCpForHeatmap(s)) : runningSegmentBarCssColor(s.run_intensity_class))
                  : heatmapBar ? { backgroundColor: cpPercentHeatmapColor(vizSplitCpForHeatmap(s)) } : {}),
              }}
            />
          </div>
        );
      })}
    </div>
  );
}
